import {
  type MediaUploadSizeViolation,
  resolveMediaUploadSizeViolation,
  uploadMediaInputFile,
} from '@/ai/media/client/upload-media-input-file';
import type { BrowserUploadProgress } from '@/storage/client';
import {
  resolveDeclaredImageMimeType,
  shouldLoadEnhancerUploadPreprocessor,
} from '../upload-image-preprocess-policy';
import { readClientImageDimensions } from './client-image-meta';
import type { PreparedEnhancerUploadFile } from './upload-image-preprocess';

export type EnhancerImageDimensionLimits = {
  maxWidth?: number;
  maxHeight?: number;
  maxMegapixels?: number;
};

export class EnhancerUploadFileTooLargeError extends Error {
  readonly maxBytes: number;
  readonly sizeBytes: number;

  constructor(
    message: string,
    params: { maxBytes: number; sizeBytes: number }
  ) {
    super(message);
    this.name = 'EnhancerUploadFileTooLargeError';
    this.maxBytes = params.maxBytes;
    this.sizeBytes = params.sizeBytes;
  }
}

export function isEnhancerUploadFileTooLargeError(
  error: unknown
): error is EnhancerUploadFileTooLargeError {
  return error instanceof EnhancerUploadFileTooLargeError;
}

function throwEnhancerUploadFileTooLargeError(
  violation: MediaUploadSizeViolation,
  message?: string
): never {
  throw new EnhancerUploadFileTooLargeError(
    message ?? 'File size exceeds the allowed limit',
    violation
  );
}

export class EnhancerUploadImageDimensionsTooLargeError extends Error {
  constructor() {
    super('Image dimensions exceed the allowed limit');
    this.name = 'EnhancerUploadImageDimensionsTooLargeError';
  }
}

export function isEnhancerUploadImageDimensionsTooLargeError(
  error: unknown
): error is EnhancerUploadImageDimensionsTooLargeError {
  return error instanceof EnhancerUploadImageDimensionsTooLargeError;
}

export function isEnhancerImageDimensionsTooLarge(
  dimensions: { width: number; height: number },
  limits: EnhancerImageDimensionLimits
) {
  return (
    (limits.maxWidth !== undefined && dimensions.width > limits.maxWidth) ||
    (limits.maxHeight !== undefined && dimensions.height > limits.maxHeight) ||
    (limits.maxMegapixels !== undefined &&
      dimensions.width * dimensions.height > limits.maxMegapixels * 1_000_000)
  );
}

async function loadPrepareEnhancerUploadFile() {
  const mod = await import('./upload-image-preprocess');
  return mod.prepareEnhancerUploadFile;
}

function canonicalizeEnhancerUploadFileMimeType(file: File) {
  const canonicalMimeType = resolveDeclaredImageMimeType(file);
  const declaredMimeType = file.type.split(';')[0]?.trim().toLowerCase();
  if (!canonicalMimeType || canonicalMimeType === declaredMimeType) {
    return file;
  }

  return new File([file], file.name, {
    type: canonicalMimeType,
    lastModified: file.lastModified,
  });
}

async function prepareEnhancerUploadFileIfNeeded(params: {
  file: File;
  maxBytes?: number;
  supportedMimeTypes?: readonly string[];
}) {
  const canonicalFile = canonicalizeEnhancerUploadFileMimeType(params.file);
  if (
    !shouldLoadEnhancerUploadPreprocessor(
      canonicalFile,
      params.supportedMimeTypes
    )
  ) {
    return {
      file: canonicalFile,
      changed: canonicalFile !== params.file,
    };
  }

  const prepareEnhancerUploadFile = await loadPrepareEnhancerUploadFile();
  return prepareEnhancerUploadFile(canonicalFile, {
    maxBytes: params.maxBytes,
    supportedMimeTypes: params.supportedMimeTypes,
  });
}

export async function uploadEnhancerInputFile(params: {
  file: File;
  folder?: string;
  maxBytes?: number;
  maxBytesErrorMessage?: string;
  supportedMimeTypes?: readonly string[];
  imageDimensionLimits?: EnhancerImageDimensionLimits;
  prepared?: PreparedEnhancerUploadFile;
  onProgress?: (progress: BrowserUploadProgress) => void;
  guestTask?: {
    pendingRunId: string;
    inputSlotId: string;
    paymentIntentId?: string;
    guestRecoveryToken?: string;
  };
}) {
  const sourceSizeViolation = resolveMediaUploadSizeViolation(
    params.file,
    params.maxBytes
  );
  if (sourceSizeViolation) {
    throwEnhancerUploadFileTooLargeError(
      sourceSizeViolation,
      params.maxBytesErrorMessage
    );
  }

  const prepared =
    params.prepared ??
    (await prepareEnhancerUploadFileIfNeeded({
      file: params.file,
      maxBytes: params.maxBytes,
      supportedMimeTypes: params.supportedMimeTypes,
    }));

  const preparedSizeViolation = resolveMediaUploadSizeViolation(
    prepared.file,
    params.maxBytes
  );
  if (preparedSizeViolation) {
    throwEnhancerUploadFileTooLargeError(
      preparedSizeViolation,
      params.maxBytesErrorMessage
    );
  }

  if (
    params.imageDimensionLimits &&
    isEnhancerImageDimensionsTooLarge(
      await readClientImageDimensions(prepared.file),
      params.imageDimensionLimits
    )
  ) {
    throw new EnhancerUploadImageDimensionsTooLargeError();
  }

  const upload = await uploadMediaInputFile({
    file: prepared.file,
    folder: params.folder,
    guestTask: params.guestTask,
    onProgress: params.onProgress,
  });

  return {
    ...upload,
    uploadedFile: prepared.file,
    fileWasNormalized: prepared.changed,
  };
}
